// An issue's sessions, newest first, each a link to its page. The action
// beneath the issue's title opens the Code tab's New session dialog, filled
// from the issue. A review the queue started links to its proposal too.
import { Link, useNavigate } from 'react-router-dom'
import type { Issue } from '../api/types'
import { NewSessionDialog } from '../code/NewSession'
import { SectionHeader } from '../components/layout'
import { Time } from '../components/Time'
import { useSessionPath } from '../projects/ProjectRoute'
import { useBoardState } from '../store/context'
import { needsServer } from '../components/needsServer'
import { sessionPath } from '../projects/routing'
import { proposalPath } from '../queue/words'
import { sessionsOf, stateName } from './words'
import { useWorkspaceDraft } from '../workspace/drafts'

export function IssueSessions({ issue }: { issue: Issue }) {
  const { sessions, projects } = useBoardState()
  const pathOf = useSessionPath()
  const projectKey = projects.get(issue.project)?.key
  const list = sessionsOf(sessions, issue.id)
  return (
    <section className="section issue-sessions">
      <SectionHeader title="Sessions" count={list.length} />
      {list.length > 0 ? (
        <ul className="session-list">
          {list.map((session) => (
            <li key={session.id} className="session-list-item">
              <Link className="session-row" to={pathOf(session.id)}>
                <span className="session-row-agent">{session.agent.model}</span>
                <span className={`session-state is-${session.state}`}>{stateName(session.state)}</span>
                <Time iso={session.created_at} className="session-row-time" />
              </Link>
              {session.origin.kind === 'review' && (
                <Link className="tag session-row-origin" to={proposalPath(session.origin.proposal, projectKey)}>
                  Review of a proposal
                </Link>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No sessions yet.</p>
      )}
    </section>
  )
}

/** Today's action beneath the title, using the issue's own project. */
export function IssueSessionAction({ issue }: { issue: Issue }) {
  const { projects, offline } = useBoardState()
  const [starting, setStarting] = useWorkspaceDraft('session.new.open', false, (value): value is boolean => typeof value === 'boolean', () => false)
  const navigate = useNavigate()
  const pathOf = useSessionPath()
  const projectKey = projects.get(issue.project)?.key
  if (issue.archived_at) return null
  return <>
    <p className="issue-actions"><button type="button" className="button button-small button-session" disabled={offline} {...needsServer(offline)} onClick={() => setStarting(true)}>Start a session</button></p>
    {starting && (
      <NewSessionDialog
        projectId={issue.project}
        issue={issue}
        onClose={() => setStarting(false)}
        onStarted={(session) => {
          setStarting(false)
          navigate(projectKey ? sessionPath(projectKey, session.id) : pathOf(session.id))
        }}
      />
    )}
  </>
}
